// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { color, mergeWithCss } from "@plumberssupply/XDL/Utilities";
import React, { FC, useMemo, useState } from "react";
import { css } from "styled-components";

interface ProductGridLayoutContextType {
    styles: ProductGridLayoutStyle;
}
export interface ProductGridLayoutStyle {
    container?: InjectableCss;
    info?: InjectableCss;
    priceSection?: InjectableCss;
    availabilitySection?: InjectableCss;
    addToListSection?: InjectableCss;
    addToCartSection?: InjectableCss;
    image?: InjectableCss;
}

const productGridLayoutStyle: ProductGridLayoutStyle = {
    container: {
        css: css`
            position: relative;
            display: flex;
            max-width: 418px;
            width: 100%;
            height: 100%;
            margin: 0 auto;
            flex-wrap: wrap;
            min-height: 215px;
        `,
    },
    image: {
        css: css`
            position: relative;
            background-color: ${color("common.white")};
            display: flex;
            flex-direction: column;
            margin-right: 20px;
            width: 80px;
            img {
                max-width: 100%;
                max-height: 100%;
                height: auto;
                object-fit: contain;
                margin-bottom: 12px;
            }
        `,
    },
    info: {
        css: css`
            display: flex;
            flex-direction: column;
            margin-bottom: 12px;
        `,
    },
    priceSection: {
        css: css`
            display: flex;
            flex-direction: column;
            margin-top: auto;
            margin-bottom: 12px;
            min-height: 92px;
        `,
    },
    availabilitySection: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 8px;
        `,
    },
    addToCartSection: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            justify-content: space-between;
            align-items: center;
        `,
    },
    addToListSection: {
        css: css`
            align-items: center;
            display: flex;
            justify-content: space-between;
        `,
    },
};

const ProductGridLayoutContext = React.createContext<ProductGridLayoutContextType>({
    styles: {},
});

export const useProductGridLayoutContext = () => {
    const context = React.useContext(ProductGridLayoutContext);
    if (!context) {
        throw new Error("ProductGridLayout compound components cannot be rendered outside ProductGridLayout");
    }
    return context;
};

const ProductGridLayout = ({
    children,
    extendedStyles,
    ...otherProps
}: {
    children: Array<React.ReactElement>;
    extendedStyles?: ProductGridLayoutStyle;
    [key: string]: any;
}) => {
    const [styles] = useState(() => mergeWithCss(productGridLayoutStyle, extendedStyles));
    const value = useMemo(() => ({ styles }), [styles]);
    return (
        <ProductGridLayoutContext.Provider value={value}>
            <StyledWrapper {...styles.container} {...otherProps}>
                {children}
            </StyledWrapper>
        </ProductGridLayoutContext.Provider>
    );
};

const Image: FC = ({ children, ...otherProps }) => {
    const {
        styles: { image },
    } = useProductGridLayoutContext();

    return (
        <StyledWrapper {...otherProps} {...image}>
            {children}
        </StyledWrapper>
    );
};

const Info: FC = ({ children, ...otherProps }) => {
    const {
        styles: { info },
    } = useProductGridLayoutContext();

    return (
        <StyledWrapper {...otherProps} {...info}>
            {children}
        </StyledWrapper>
    );
};

const PriceSection: FC = ({ children, ...otherProps }) => {
    const {
        styles: { priceSection },
    } = useProductGridLayoutContext();

    return (
        <StyledWrapper {...otherProps} {...priceSection}>
            {children}
        </StyledWrapper>
    );
};

const AvailabilitySection: FC = ({ children, ...otherProps }) => {
    const {
        styles: { availabilitySection },
    } = useProductGridLayoutContext();

    return (
        <StyledWrapper {...otherProps} {...availabilitySection}>
            {children}
        </StyledWrapper>
    );
};

const AddToCartSection: FC = ({ children, ...otherProps }) => {
    const {
        styles: { addToCartSection },
    } = useProductGridLayoutContext();

    return (
        <StyledWrapper {...otherProps} {...addToCartSection}>
            {children}
        </StyledWrapper>
    );
};

const AddToListAction: FC = ({ children, ...otherProps }) => {
    const {
        styles: { addToListSection },
    } = useProductGridLayoutContext();

    return (
        <StyledWrapper {...otherProps} {...addToListSection} data-test-selector="productCarousel-AddToListActions">
            {children}
        </StyledWrapper>
    );
};

ProductGridLayout.Image = Image;
ProductGridLayout.Info = Info;
ProductGridLayout.PriceSection = PriceSection;
ProductGridLayout.AvailabilitySection = AvailabilitySection;
ProductGridLayout.AddToCartSection = AddToCartSection;
ProductGridLayout.AddToListAction = AddToListAction;

export default ProductGridLayout;
